import React, { useState } from "react";
import { useRacing } from "../context/RacingContext";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { UserPlus, Trash2, Mail, Users } from "lucide-react";
import { formatApiError } from "../lib/api";

const AVATAR = "https://images.unsplash.com/photo-1552255440-ac1fa0a5b2a3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwyfHxyYWNlJTIwY2FyJTIwZHJpdmVyJTIwcG9ydHJhaXR8ZW58MHx8fHwxNzgzNTU4ODgzfDA&ixlib=rb-4.1.0&q=85";

export default function PitCrew() {
  const { pitCrew, addPitCrewMember, removePitCrewMember } = useRacing();
  const { user } = useAuth();
  const locked = user?.trialLocked;
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const inputCls = "w-full rounded-lg border border-mt-border bg-mt-card px-3 py-2.5 text-sm outline-none focus:border-mt-primary focus:ring-1 focus:ring-mt-primary";

  const add = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return toast.error("Name and email required");
    setBusy(true);
    try {
      const res = await addPitCrewMember({ name: name.trim(), email: email.trim() });
      toast.success(res.emailSent ? "Invite sent!" : "Crew member added (email invite pending setup)");
      setName(""); setEmail("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to add");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <h1 className="font-head text-3xl font-black tracking-tighter">Pit Crew</h1>
        <p className="mt-1 text-sm text-mt-mutedfg">Invite your crew to follow your season.</p>
      </div>

      <form onSubmit={add} className="space-y-3 rounded-xl border border-mt-border bg-mt-card p-5 shadow-sm" data-testid="pitcrew-form">
        <div className="grid gap-3 sm:grid-cols-2">
          <input className={inputCls} placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} data-testid="pitcrew-name" />
          <input className={inputCls} type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="pitcrew-email" />
        </div>
        <Button type="submit" disabled={busy || locked} data-testid="pitcrew-add" className="w-full gap-2 bg-mt-primary text-white hover:bg-mt-primary/90">
          <UserPlus size={16} /> {locked ? "Upgrade to add crew" : "Add & Invite"}
        </Button>
      </form>

      {pitCrew.length === 0 ? (
        <div className="rounded-xl border border-dashed border-mt-border bg-mt-card p-10 text-center text-mt-mutedfg" data-testid="pitcrew-empty">
          <Users size={32} className="mx-auto" />
          <p className="mt-3">No crew members yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {pitCrew.map((m) => (
            <div key={m.id} data-testid={`pitcrew-member-${m.id}`} className="flex items-center gap-3 rounded-xl border border-mt-border bg-mt-card p-3 shadow-sm">
              <img src={AVATAR} alt="" className="h-11 w-11 rounded-full object-cover" />
              <div className="min-w-0 flex-1">
                <div className="font-head font-bold">{m.name}</div>
                <div className="flex items-center gap-1 truncate text-xs text-mt-mutedfg"><Mail size={11} /> {m.email}</div>
              </div>
              <span className="hidden text-xs text-mt-mutedfg sm:block">{m.addedDate}</span>
              <button onClick={() => removePitCrewMember(m.id)} data-testid={`pitcrew-remove-${m.id}`} className="rounded-md p-2 text-mt-mutedfg hover:bg-mt-secondary hover:text-mt-text">
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
